import { getTextControlSelectionRect } from '../utils/textareaSelectionRect';
import { TranslateError, translateText } from './translateApi';
import { sentenceAtOffset, selectionMidOffsetInElement } from './sentenceAtPoint';
import { freezeAnchorRect, unionRangeClientRect } from './translateAnchor';
import { tryAddSpanishReviewWord } from './spanishReview';
import {
  hideTranslateBubble,
  isTranslateBubblePinned,
  showTranslateBubbleError,
  showTranslateBubbleLoading,
  showTranslateBubbleResult,
  type TranslateBubbleResultOptions,
} from './translateBubble';

const MIN_PHRASE_LEN = 2;
const MAX_PHRASE_LEN = 500;

let abort: AbortController | null = null;
const cache = new Map<string, string>();

export type TextControl = HTMLTextAreaElement | HTMLInputElement;

type Scope =
  | { kind: 'content'; el: HTMLElement }
  | { kind: 'control'; control: TextControl };

const scopes: Scope[] = [];
let selectionWatchBound = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastPromptPhrase = '';

function isCoarsePointer(): boolean {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

function selectionCheckDelayMs(): number {
  return isCoarsePointer() ? 500 : 80;
}

function normalizePhrase(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

function bubbleContains(node: Node): boolean {
  const bubble = document.getElementById('translate-selection-bubble');
  return bubble != null && bubble.contains(node);
}

function selectionAnchorRect(el: HTMLElement): DOMRect {
  const box = el.getBoundingClientRect();
  const lineHeight =
    parseFloat(getComputedStyle(el).lineHeight) ||
    parseFloat(getComputedStyle(el).fontSize) * 1.2 ||
    18;
  const top = Math.min(box.bottom - lineHeight, box.top + lineHeight);
  return new DOMRect(box.left + box.width / 2, top, 0, lineHeight);
}

function selectionInTextControl(
  control: TextControl
): { phrase: string; rect: DOMRect } | null {
  const start = control.selectionStart ?? 0;
  const end = control.selectionEnd ?? 0;
  if (start === end) return null;

  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const phrase = normalizePhrase(control.value.slice(lo, hi));
  const rect = getTextControlSelectionRect(control, lo, hi);

  if (phrase.length < MIN_PHRASE_LEN) return null;
  if (phrase.length > MAX_PHRASE_LEN) {
    showTranslateBubbleError(
      rect,
      `${phrase.slice(0, 40)}…`,
      `Selection is too long (max ${MAX_PHRASE_LEN} characters).`
    );
    return null;
  }

  return { phrase, rect: freezeAnchorRect(rect) };
}

function selectionInContentElement(
  el: HTMLElement
): { phrase: string; rect: DOMRect } | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

  const anchor = sel.anchorNode;
  const focus = sel.focusNode;
  if (!anchor || !focus) return null;
  if (!el.contains(anchor) || !el.contains(focus)) return null;

  const phrase = normalizePhrase(sel.toString());
  if (phrase.length < MIN_PHRASE_LEN) return null;

  const range = sel.getRangeAt(0);
  let rect = unionRangeClientRect(range);
  if (rect.width === 0 && rect.height === 0) {
    rect = selectionAnchorRect(el);
  }
  rect = freezeAnchorRect(rect);

  if (phrase.length > MAX_PHRASE_LEN) {
    showTranslateBubbleError(
      rect,
      `${phrase.slice(0, 40)}…`,
      `Selection is too long (max ${MAX_PHRASE_LEN} characters).`
    );
    return null;
  }

  return { phrase, rect };
}

function hitFromScope(scope: Scope): { phrase: string; rect: DOMRect } | null {
  return scope.kind === 'content'
    ? selectionInContentElement(scope.el)
    : selectionInTextControl(scope.control);
}

function sentenceForScope(scope: Scope): string | null {
  if (scope.kind === 'control') {
    const start = scope.control.selectionStart ?? 0;
    const end = scope.control.selectionEnd ?? 0;
    if (start === end) return null;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    return sentenceAtOffset(scope.control.value, Math.floor((lo + hi) / 2));
  }
  const mid = selectionMidOffsetInElement(scope.el);
  if (mid === null) return null;
  return sentenceAtOffset(scope.el.textContent ?? '', mid);
}

function findActiveHitWithSentence(): { phrase: string; rect: DOMRect; sentence: string } | null {
  for (const scope of scopes) {
    const hit = hitFromScope(scope);
    if (!hit) continue;
    const sentence = sentenceForScope(scope) ?? hit.phrase;
    return { ...hit, sentence };
  }
  return null;
}

function selectionDiffersFromSentence(phrase: string, sentence: string): boolean {
  return normalizePhrase(phrase) !== normalizePhrase(sentence);
}

function bubbleResultOptions(highlightedWord?: string): TranslateBubbleResultOptions | undefined {
  if (!highlightedWord?.trim()) return undefined;
  return { highlightedWord: highlightedWord.trim() };
}

/** Auto-translate; word highlight translates the surrounding sentence. */
export function translateWithSentenceFollowUp(
  phrase: string,
  rect: DOMRect,
  highlightedWord?: string
): void {
  void runTranslate(phrase, rect, highlightedWord);
}

async function runTranslate(phrase: string, rect: DOMRect, highlightedWord?: string): Promise<void> {
  const anchor = freezeAnchorRect(rect);
  abort?.abort();
  abort = new AbortController();
  const resultOpts = bubbleResultOptions(highlightedWord);
  const cached = cache.get(phrase);
  if (cached) {
    tryAddSpanishReviewWord(highlightedWord ?? phrase, phrase);
    showTranslateBubbleResult(anchor, phrase, cached, resultOpts);
    return;
  }

  showTranslateBubbleLoading(anchor, phrase);
  try {
    const translated = await translateText(phrase, { signal: abort.signal });
    cache.set(phrase, translated);
    tryAddSpanishReviewWord(highlightedWord ?? phrase, phrase);
    showTranslateBubbleResult(anchor, phrase, translated, resultOpts);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    const msg =
      err instanceof TranslateError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Translation failed.';
    if (msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')) {
      showTranslateBubbleError(
        anchor,
        phrase,
        'Could not reach the translation service (network). Check your connection and try again.'
      );
      return;
    }
    showTranslateBubbleError(anchor, phrase, msg);
  }
}

function presentHit(hit: { phrase: string; rect: DOMRect; sentence: string }): void {
  if (hit.phrase === lastPromptPhrase && isTranslateBubblePinned()) return;
  lastPromptPhrase = hit.phrase;

  const wordInSentence = selectionDiffersFromSentence(hit.phrase, hit.sentence);
  const toTranslate = wordInSentence ? hit.sentence : hit.phrase;
  const highlightedWord = wordInSentence ? hit.phrase : undefined;
  void runTranslate(toTranslate, hit.rect, highlightedWord);
}

function runSelectionCheck(): void {
  debounceTimer = null;
  const hit = findActiveHitWithSentence();
  if (!hit) {
    if (isTranslateBubblePinned()) return;
    lastPromptPhrase = '';
    hideTranslateBubble();
    return;
  }
  presentHit(hit);
}

function scheduleSelectionCheck(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSelectionCheck, selectionCheckDelayMs());
}

function onDocumentSelectionChange(): void {
  scheduleSelectionCheck();
}

function onDocumentPointerDown(e: PointerEvent): void {
  const t = e.target as Node;
  if (bubbleContains(t)) return;
  lastPromptPhrase = '';
  hideTranslateBubble();
}

function ensureSelectionWatch(): void {
  if (selectionWatchBound) return;
  selectionWatchBound = true;
  document.addEventListener('selectionchange', onDocumentSelectionChange);
  document.addEventListener('touchend', onDocumentSelectionChange, { passive: true });
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
}

function teardownSelectionWatchIfIdle(): void {
  if (scopes.length > 0 || !selectionWatchBound) return;
  document.removeEventListener('selectionchange', onDocumentSelectionChange);
  document.removeEventListener('touchend', onDocumentSelectionChange);
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  selectionWatchBound = false;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

function registerScope(scope: Scope): () => void {
  scopes.push(scope);
  ensureSelectionWatch();
  return () => {
    const i = scopes.indexOf(scope);
    if (i >= 0) scopes.splice(i, 1);
    teardownSelectionWatchIfIdle();
  };
}

/** Textarea / input in the item editor modal. */
export function attachTextControlSelectionTranslate(control: TextControl): () => void {
  const scope: Scope = { kind: 'control', control };
  const unregister = registerScope(scope);

  const onKeyDown = (e: Event) => {
    if (e instanceof KeyboardEvent && e.key === 'Escape') hideTranslateBubble();
  };
  control.addEventListener('keydown', onKeyDown);

  return () => {
    control.removeEventListener('keydown', onKeyDown);
    unregister();
  };
}

/** Read-only item title / body on the page. */
export function attachContentSelectionTranslate(el: HTMLElement): () => void {
  return registerScope({ kind: 'content', el });
}

let modalDetach: (() => void) | null = null;

export function attachModalSelectionTranslate(controls: TextControl[]): void {
  detachModalSelectionTranslate();

  const cleanups = controls.map(c => attachTextControlSelectionTranslate(c));
  modalDetach = () => {
    for (const fn of cleanups) fn();
  };
}

export function detachModalSelectionTranslate(): void {
  modalDetach?.();
  modalDetach = null;
  abort?.abort();
  abort = null;
  lastPromptPhrase = '';
  hideTranslateBubble();
}

/** @deprecated Use attachTextControlSelectionTranslate */
export function attachEditorSelectionTranslate(textarea: HTMLTextAreaElement): void {
  attachModalSelectionTranslate([textarea]);
}

/** @deprecated Use detachModalSelectionTranslate */
export function detachEditorSelectionTranslate(): void {
  detachModalSelectionTranslate();
}

/** Wire highlight-to-translate on item text (list + page). */
export function wireItemTextSelectionTranslate(root: ParentNode): void {
  const seen = new Set<HTMLElement>();
  for (const el of root.querySelectorAll<HTMLElement>('.item-text, .item-title, .item-body')) {
    if (seen.has(el)) continue;
    seen.add(el);
    attachContentSelectionTranslate(el);
  }
}
